import isEqual from 'fast-deep-equal';
import debounce from 'lodash/debounce';
import { create } from 'zustand';

import { queryClient } from '@app/queryClient';
import { runCreateModelView, runUpdateModelView } from '@mpm-druid/api';
import {
  MODEL_DASHBOARD_AUTO_SAVE_DELAY,
  MODEL_DASHBOARD_DEFAULT_PANEL_HEIGHT,
  MODEL_DASHBOARD_DEFAULT_PANEL_WIDTH
} from '@mpm-druid/constants';
import {
  ModelView,
  Panel,
  PanelSectionLayout,
  UnsavedModelView
} from '@mpm-druid/types';
import {
  calculateAddPanelToLayout,
  DeepPartial,
  generateDefaultView,
  mergeDeep
} from '@mpm-druid/utils';

type ModelViewStore = {
  isViewInitialized: boolean;
  savingViewId: string | null;
  view: ModelView | UnsavedModelView;
  cancelAutoSave: () => void;
  flushAutoSave: () => void;
  setView: (view: ModelView | UnsavedModelView) => void;
  updateView: (updates: DeepPartial<ModelView>) => void;
  addSectionPanel: (sectionId: string, panel: Panel) => void;
  editSectionPanel: (sectionId: string, panel: Panel) => void;
  removeSectionPanel: (sectionId: string, panelId: string) => void;
  editSectionLayout: (sectionId: string, layout: PanelSectionLayout) => void;
};

export const useModelViewStore = create<ModelViewStore>(set => {
  const debouncedSetAutoSaveView = debounce(
    async (view: ModelView | UnsavedModelView) => {
      try {
        if (view.id) {
          await runUpdateModelView(queryClient, {
            modelId: view.model_id,
            partialView: {
              name: view.name,
              data: view.data,
              version: view.version
            },
            viewId: view.id
          });
        } else {
          await runCreateModelView(queryClient, {
            modelId: view.model_id,
            view: {
              name: view.name,
              data: view.data,
              version: view.version
            }
          });
        }
      } finally {
        set({ savingViewId: null });
      }
    },
    MODEL_DASHBOARD_AUTO_SAVE_DELAY,
    { maxWait: MODEL_DASHBOARD_AUTO_SAVE_DELAY * 5 }
  );

  return {
    isViewInitialized: false,
    savingViewId: null,
    view: generateDefaultView(''),
    setView: (view: ModelView | UnsavedModelView) => {
      set({ isViewInitialized: true, view });
    },
    updateView: (updates: DeepPartial<ModelView>) => {
      set(state => {
        const updatedView = mergeDeep(state.view, updates);
        debouncedSetAutoSaveView(updatedView);

        return { savingViewId: updatedView.id, view: updatedView };
      });
    },
    addSectionPanel: (sectionId: string, item: Panel) => {
      set(state => {
        const sections = state.view.data.sections;
        const section = sections.find(section => section.id === sectionId);
        if (!section) return state;

        const updatedSection = {
          ...section,
          items: [...section.items, item],
          layout: [
            ...section.layout,
            calculateAddPanelToLayout(
              item.id,
              MODEL_DASHBOARD_DEFAULT_PANEL_WIDTH,
              MODEL_DASHBOARD_DEFAULT_PANEL_HEIGHT,
              section.layout
            )
          ]
        };

        const updatedView = {
          ...state.view,
          data: {
            ...state.view.data,
            sections: sections.map(s =>
              s.id === sectionId ? updatedSection : s
            )
          }
        };
        debouncedSetAutoSaveView(updatedView);

        return { savingViewId: updatedView.id, view: updatedView };
      });
    },
    editSectionPanel: (sectionId: string, item: Panel) => {
      set(state => {
        const sections = state.view.data.sections;
        const section = sections.find(section => section.id === sectionId);
        if (!section) return state;

        const updatedSection = {
          ...section,
          items: section.items.map(p => (p.id === item.id ? item : p))
        };
        if (isEqual(updatedSection, section)) return state;

        const updatedView = {
          ...state.view,
          data: {
            ...state.view.data,
            sections: sections.map(s =>
              s.id === sectionId ? updatedSection : s
            )
          }
        };
        debouncedSetAutoSaveView(updatedView);

        return { savingViewId: updatedView.id, view: updatedView };
      });
    },
    removeSectionPanel: (sectionId: string, itemId: string) => {
      set(state => {
        const sections = state.view.data.sections;
        const section = sections.find(section => section.id === sectionId);
        if (!section) return state;

        const updatedSection = {
          ...section,
          items: section.items.filter(p => p.id !== itemId)
        };
        if (isEqual(updatedSection, section)) return state;

        const updatedView = {
          ...state.view,
          data: {
            ...state.view.data,
            sections: sections.map(s =>
              s.id === sectionId ? updatedSection : s
            )
          }
        };
        debouncedSetAutoSaveView(updatedView);

        return { savingViewId: updatedView.id, view: updatedView };
      });
    },
    editSectionLayout: (sectionId: string, layout: PanelSectionLayout) => {
      set(state => {
        const sections = state.view.data.sections;
        const section = sections.find(section => section.id === sectionId);
        if (!section) return state;

        const updatedSection = { ...section, layout };
        if (isEqual(updatedSection, section)) return state;

        const updatedView = {
          ...state.view,
          data: {
            ...state.view.data,
            sections: sections.map(s =>
              s.id === sectionId ? updatedSection : s
            )
          }
        };
        debouncedSetAutoSaveView(updatedView);

        return { savingViewId: updatedView.id, view: updatedView };
      });
    },
    cancelAutoSave: () => {
      debouncedSetAutoSaveView.cancel();
      set({ savingViewId: null });
    },
    flushAutoSave: () => debouncedSetAutoSaveView.flush()
  };
});
